import { OfferNetworkProvider } from "@redotech/crdb-enums/marketing/offer-network-provider";
import { ResolvedOfferConfig } from "@redotech/order-tracking-common/offer-network/offer-placement";
import { TrackingPageOffersBlock } from "@redotech/order-tracking-common/tracking-page-definition";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import CoinsStackedIcon from "@redotech/redo-icons/coins-stacked-03.svg";
import { prettyOfferNetworkProvider } from "@redotech/redo-integrations-definitions-common/offer-network/offer-placement";
import { TrackableType } from "@redotech/redo-order-tracking-trackable-common/trackable";
import { memo, useContext, useEffect, useMemo, useRef } from "react";
import { Flex } from "../../flex";
import { mountOfferPlacement } from "../../offer-network/mount-offer-placement";
import {
  buildOfferAttributes,
  OfferNetworkAttributes,
} from "../../offer-network/offer-attributes";
import { ROKT_PLACEHOLDER_ID } from "../../offer-network/rokt-adapter";
import { Text } from "../../text";
import {
  TrackingPageAccessMode,
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../context/tracking-page-context";
import { TrackingPageRuntimeLayoutContext } from "../context/tracking-page-runtime-layout-context";
import * as offersCss from "./offers.module.css";
import { observeBlockContentHeight } from "./runtime-block-height";
import { BlockWrapper } from "./shared/block-wrapper";

export const Offers = memo(function Offers(block: TrackingPageOffersBlock) {
  const context = useRequiredContext(TrackingPageContext);
  const { order, pageSettings, environment } = context;
  const { i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const runtimeLayout = useContext(TrackingPageRuntimeLayoutContext);
  const reportBlockHeight = runtimeLayout?.reportBlockHeight;
  const isTrackingCodeOnly =
    context.environment === TrackingPageEnvironment.CUSTOMER_PORTAL &&
    context.accessMode === TrackingPageAccessMode.TRACKING_CODE_ONLY;

  // Builder previews send a throwaway Redo email instead of the real customer's,
  // so offers render without leaking a shopper address to the provider.
  const previewEmail = useMemo(
    () =>
      environment === TrackingPageEnvironment.BUILDER
        ? `preview-${crypto.randomUUID()}@redo.com`
        : undefined,
    [environment],
  );

  const attributes: OfferNetworkAttributes | undefined = useMemo(
    () =>
      order && !isTrackingCodeOnly
        ? buildOfferAttributes(order, i18n.language, previewEmail)
        : undefined,
    [order, i18n.language, isTrackingCodeOnly, previewEmail],
  );

  const builderOfferNetwork =
    context.environment === TrackingPageEnvironment.BUILDER
      ? context.offerNetwork
      : undefined;
  const builderConfigLoad = useLoad(
    async (signal): Promise<ResolvedOfferConfig | undefined> => {
      if (!builderOfferNetwork || !block.offerPlacementId) {
        return undefined;
      }
      const placements = await builderOfferNetwork.listPlacements({
        provider: block.provider,
      });
      if (signal.aborted) {
        return undefined;
      }
      return placements.find((p) => p.id === block.offerPlacementId)?.config;
    },
    [builderOfferNetwork, block.provider, block.offerPlacementId],
  );

  const config: ResolvedOfferConfig | undefined =
    context.environment === TrackingPageEnvironment.CUSTOMER_PORTAL
      ? block.offerPlacementId
        ? context.resolvedOffers?.[block.offerPlacementId]
        : undefined
      : builderConfigLoad.value;

  const live = !!config && !!attributes;

  const containerId =
    block.provider === OfferNetworkProvider.Rokt
      ? ROKT_PLACEHOLDER_ID
      : `offers-${block._id}`;

  useEffect(() => {
    if (
      environment !== TrackingPageEnvironment.CUSTOMER_PORTAL ||
      live ||
      !reportBlockHeight
    ) {
      return;
    }

    reportBlockHeight(block._id, 0);
    return () => reportBlockHeight(block._id, null);
  }, [block._id, environment, live, reportBlockHeight]);

  useEffect(() => {
    const container = containerRef.current;
    if (!config || !attributes || !order || !container) {
      return;
    }
    if (
      config.provider === OfferNetworkProvider.Rokt &&
      document.getElementById(ROKT_PLACEHOLDER_ID) !== container
    ) {
      reportBlockHeight?.(block._id, 0);
      return () => reportBlockHeight?.(block._id, null);
    }

    // Builder previews force Rokt sandbox mode so no impressions record
    const mountConfig: ResolvedOfferConfig =
      config.provider === OfferNetworkProvider.Rokt &&
      environment === TrackingPageEnvironment.BUILDER
        ? { ...config, sandbox: true }
        : config;
    const logPageClick =
      context.environment === TrackingPageEnvironment.CUSTOMER_PORTAL
        ? context.productUpsell.logPageClick
        : undefined;
    let stopObservingHeight: (() => void) | undefined;
    let cancelled = false;
    let dispose: (() => void) | undefined;
    mountOfferPlacement({ config: mountConfig, attributes, container })
      .then((handle) => {
        if (cancelled) {
          handle.dispose();
          return;
        }
        dispose = handle.dispose;
        // The min-height only gave the SDK room to initialize; drop it now so
        // the block hugs the rendered offer instead of stranding whitespace.
        container.style.minHeight = "0px";
        if (
          environment === TrackingPageEnvironment.CUSTOMER_PORTAL &&
          reportBlockHeight
        ) {
          stopObservingHeight = observeBlockContentHeight(container, (height) =>
            reportBlockHeight(block._id, height),
          );
        }
        void logPageClick?.({
          trackableId: order.internalMongoId,
          trackableType: TrackableType.ORDER,
          eventType: "offer-impression",
          url: block.provider,
        }).catch(() => {});
      })
      .catch((error) => {
        if (!cancelled) {
          reportBlockHeight?.(block._id, 0);
        }
        console.warn("Failed to load offer placement", error);
      });
    return () => {
      cancelled = true;
      stopObservingHeight?.();
      reportBlockHeight?.(block._id, null);
      container.style.minHeight = "";
      try {
        dispose?.();
      } catch {
        // Never let SDK teardown break the page
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    live,
    environment,
    block._id,
    block.offerPlacementId,
    order?.internalMongoId,
    reportBlockHeight,
  ]);

  const container = live && (
    <div className={offersCss.offerCenter}>
      <div
        className={
          block.provider === OfferNetworkProvider.Falcon
            ? offersCss.falconContainer
            : offersCss.roktContainer
        }
        id={containerId}
        ref={containerRef}
      />
    </div>
  );

  if (environment === TrackingPageEnvironment.CUSTOMER_PORTAL) {
    return container || null;
  }

  return (
    <BlockWrapper
      environment={environment}
      pageSettings={pageSettings}
      transparentBackground
      withBorder={false}
    >
      {container || (
        <Flex
          align="center"
          dir="column"
          gap="xs"
          height="full"
          justify="center"
          p="xl"
        >
          <CoinsStackedIcon height={32} width={32} />
          <Text fontSize="sm" textColor="secondary">
            {prettyOfferNetworkProvider[block.provider]} offers
          </Text>
          <Text fontSize="xs" textColor="secondary">
            {block.offerPlacementId
              ? "Load an order preview to see offers"
              : "Choose a placement in the inspector"}
          </Text>
        </Flex>
      )}
    </BlockWrapper>
  );
});
